/**
 * The widened L3 contract surface (CONTRACT_VERSION 1.0.0-rc) — ADDITIVE over §6.3.
 *
 * Everything in §6.3 (the frozen Phase 1 surface) is preserved unchanged; this
 * module *adds*: new commands, new queries, the multi-format export/import
 * surface, and the lockstep enumerations used by the public reference. The shape
 * of every pre-existing command/query is untouched, so the move to 1.0.0-rc is a
 * surface *freeze*, not a breaking change.
 *
 * Design principles, unchanged since Phase 1:
 *   - commands carry intent; the Store maps them to events; the model reducer
 *     recomputes state; verdicts AND profile-effects come solely from the L1
 *     kernel (INV-CORE);
 *   - the canonical, ownership-grade log is the §6.2 `Event[]` (`exportLog`); the
 *     one app-level annotation (`ExtEvent`) rides a parallel app-log and never
 *     changes a kernel verdict (INV-ADD-NOT-ALTER).
 *
 * Phase 3 made the modal axis real: the kernel now computes the modal effect of an
 * edge import on a claim's profile (`applyEdgeToProfile`). So modal-on-edges is now
 * surfaced through the kernel via `modalEffect` (query) and `apply-edge-modal`
 * (command). The Phase 2 free-form `set-edge-modal`/`edgeModal` placeholder is kept
 * for back-compat but **@deprecated** — the first worked example of the deprecation
 * policy (see REFERENCE.md).
 */
/** Type guard: is this a frozen §6.2 (kernel-relevant, ownership-grade) event? */
export function isModelEvent(e) {
    return e.t !== 'edge-modal-set';
}
// --- lockstep enumerations (keep the public reference honest) ------------------
/**
 * Every command kind, as runtime data. The `satisfies Record<AppCommand['c'], 1>`
 * makes the compiler reject this list if it omits a command kind OR lists one that
 * does not exist — so the public reference test (which checks the doc enumerates
 * each of these) can never silently drift from the `AppCommand` union.
 */
export const COMMAND_KINDS = Object.freeze(Object.keys({
    challenge: 1,
    activate: 1,
    declare: 1,
    'import-edge': 1,
    'add-claim': 1,
    promote: 1,
    'record-lane': 1,
    'raise-flag': 1,
    reframe: 1,
    'compose-frame': 1,
    'apply-edge-modal': 1,
    'set-edge-modal': 1,
    review: 1,
    'flag-clear': 1,
    'record-machine': 1,
    'register-pack': 1,
    'record-computation': 1,
    undo: 1,
    redo: 1,
}));
/** Every `AppStore` member name, as runtime data (same lockstep guarantee). */
export const STORE_MEMBERS = Object.freeze(Object.keys({
    // §6.3 Store + Queries
    dispatch: 1,
    subscribe: 1,
    replay: 1,
    exportLog: 1,
    getState: 1,
    verdictOf: 1,
    exposure: 1,
    systemClaims: 1,
    evidenceOf: 1,
    contractVersion: 1,
    // Phase 2/3 queries
    validityUnder: 1,
    modalEffect: 1,
    lanesOf: 1,
    historyPosition: 1,
    edgeModal: 1,
    registeredPacks: 1,
    // Phase 2/3 export/import
    exportAppLog: 1,
    importAppLog: 1,
    exportJSON: 1,
    importJSON: 1,
    exportReport: 1,
}));
//# sourceMappingURL=contract2.js.map